import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PrivacyNote } from "@/components/PrivacyNote";
import { QuoteImageDialog } from "@/components/QuoteImageDialog";
import { Quote, Plus, Trash2, Share2, Heart, Sparkles, Image as ImageIcon } from "lucide-react";

export default function Favorites() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [daily, setDaily] = useState("");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sharing, setSharing] = useState(null);
  const [imageQuote, setImageQuote] = useState(null);

  const load = () => api.get("/favorites").then(({ data }) => setItems(data.favorites || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get(`/daily-quote?lang=${lang}`).then(({ data }) => setDaily(data?.quote || "")).catch(() => {});
  }, [lang]);

  const dailySaved = items.some((i) => (i.text || "").trim().toLowerCase() === daily.trim().toLowerCase());

  const add = async () => {
    if (!text.trim()) return toast.error(t("favorites.textPh"));
    try {
      await api.post("/favorites", { text, source: "custom" });
      setText(""); setOpen(false); load();
      toast.success(t("quote.saved"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveDaily = async () => {
    try { await api.post("/favorites", { text: daily, source: "ai" }); load(); toast.success(t("quote.saved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { await api.delete(`/favorites/${id}`); load(); };
  const share = async (q) => {
    setSharing(q.quote_id);
    try {
      await api.post("/community/posts", { content: q.text, group: "growth", anonymous: true, override_warning: true });
      toast.success(t("favorites.shared"));
    } catch (e) {
      toast.error(e?.response?.status === 402 ? "Upgrade to share with the community." : apiErr(e));
    } finally { setSharing(null); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("favorites.title")}</h1>
          <p className="mt-2 max-w-lg text-muted-foreground">{t("favorites.subtitle")}</p>
          <PrivacyNote className="mt-3" />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2" data-testid="add-quote-button"><Plus className="h-4 w-4" strokeWidth={1.5} /> {t("favorites.add")}</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{t("favorites.dialogTitle")}</DialogTitle>
              <DialogDescription>{t("favorites.dialogDesc")}</DialogDescription>
            </DialogHeader>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("favorites.textPh")} data-testid="quote-text-input" className="rounded-2xl resize-none" rows={3} />
            <Button onClick={add} className="rounded-full" data-testid="save-quote-button">{t("favorites.save")}</Button>
          </DialogContent>
        </Dialog>
      </div>

      {daily && !dailySaved && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="ambient-shadow mt-8 rounded-3xl border border-border/60 bg-gradient-to-b from-primary/10 to-card p-6" data-testid="favorites-today-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} /> {t("favorites.todayTitle")}
          </div>
          <p className="mt-3 font-serif text-xl italic leading-snug">“{daily}”</p>
          <Button onClick={saveDaily} variant="outline" className="mt-5 rounded-full gap-2" data-testid="save-daily-button">
            <Heart className="h-4 w-4" strokeWidth={1.5} /> {t("quote.save")}
          </Button>
        </motion.div>
      )}

      {items.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-border/60 bg-card p-10 text-center ambient-shadow" data-testid="favorites-empty">
          <Quote className="mx-auto h-8 w-8 text-primary" strokeWidth={1.5} />
          <p className="mt-4 text-muted-foreground">{t("favorites.empty")}</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((q, i) => (
            <motion.div key={q.quote_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.05 }}
              className="ambient-shadow group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-border/60 bg-card p-6" data-testid={`favorite-card-${q.quote_id}`}>
              <Quote className="absolute -right-2 -top-2 h-16 w-16 text-primary/5" strokeWidth={1.5} />
              <p className="relative font-serif text-lg italic leading-snug">“{q.text}”</p>
              <div className="relative mt-5 flex items-center justify-between">
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${q.source === "ai" ? "bg-primary/10 text-primary" : "bg-sage/15 text-sage"}`}>
                  {q.source === "ai" ? t("favorites.sourceAi") : t("favorites.sourceCustom")}
                </span>
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button size="sm" variant="ghost" className="h-8 rounded-full gap-1 text-muted-foreground hover:text-primary" onClick={() => setImageQuote(q.text)} data-testid={`image-favorite-${q.quote_id}`}>
                    <ImageIcon className="h-4 w-4" strokeWidth={1.5} /> {t("favorites.makeImage")}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 rounded-full gap-1 text-muted-foreground hover:text-primary" onClick={() => share(q)} disabled={sharing === q.quote_id} data-testid={`share-favorite-${q.quote_id}`}>
                    <Share2 className="h-4 w-4" strokeWidth={1.5} /> {t("favorites.share")}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 rounded-full p-0 text-muted-foreground hover:text-destructive" onClick={() => remove(q.quote_id)} data-testid={`remove-favorite-${q.quote_id}`}>
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <QuoteImageDialog open={!!imageQuote} onOpenChange={(o) => { if (!o) setImageQuote(null); }} text={imageQuote || ""} />
    </div>
  );
}
